import axios from "axios";
import { resolveBrand } from "../lib/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Mar 2026 · When the user is on a narrow-brand host (soulumo.app,
// onemillionlights.org, …), stamp the current brand onto auth
// requests so the backend can honour the beta allow-list and
// prevent cross-brand login bleed. On reclaim.app this returns
// undefined so Reclaim auth behaviour is unchanged.
function brandForAuth() {
  try {
    const b = resolveBrand();
    return b && b !== "reclaim" ? b : undefined;
  } catch (_) {
    return undefined;
  }
}

// Auth API
export const authAPI = {
  login: (email, password) =>
    axios.post(`${API}/auth/login`, { email, password, brand: brandForAuth() }),
  register: (name, email, password, referralCode = null) =>
    axios.post(`${API}/auth/register`, {
      name, email, password,
      referral_code: referralCode,
      brand: brandForAuth(),
    }),
  getMe: () => axios.get(`${API}/auth/me`),
  updateProfile: (data) => axios.put(`${API}/auth/profile`, data),
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
};

// Programs API
export const programsAPI = {
  getAll: () => axios.get(`${API}/programs`),
  getById: (id) => axios.get(`${API}/programs/${id}`),
  getProgress: (programId) => axios.get(`${API}/progress/program/${programId}`),
};

// Lessons API
export const lessonsAPI = {
  getById: (id) => axios.get(`${API}/lessons/${id}`),
  getReflections: (lessonId) => axios.get(`${API}/reflections/${lessonId}`),
  saveReflection: (lessonId, prompt, response) => 
    axios.post(`${API}/reflections`, { lesson_id: lessonId, prompt, response }),
  completeLesson: (lessonId, programId, moduleId) => 
    axios.post(`${API}/progress/complete-lesson`, { lesson_id: lessonId, program_id: programId, module_id: moduleId }),
};

// Meditation API
export const meditationAPI = {
  getAudio: (lessonId) => axios.get(`${API}/meditation/audio/${lessonId}`),
  generateAudio: (lessonId) => axios.post(`${API}/meditation/generate`, { lesson_id: lessonId }),
  getAmbientSounds: () => axios.get(`${API}/meditation/ambient-sounds`),
};

// Progress API
export const progressAPI = {
  getOverview: () => axios.get(`${API}/progress`),
};

// Presence API (Daily Presence feature)
export const presenceAPI = {
  getToday: () => axios.get(`${API}/presence/today`),
  complete: (reflection, promptText) => 
    axios.post(`${API}/presence/complete`, { reflection, prompt_text: promptText }),
  getHistory: (month) => axios.get(`${API}/presence/history${month ? `?month=${month}` : ''}`),
  getStats: () => axios.get(`${API}/presence/stats`),
};

// Home API
export const homeAPI = {
  getData: () => axios.get(`${API}/home`),
};

// Payments API
export const paymentsAPI = {
  createCheckout: (planId, originUrl) => 
    axios.post(`${API}/payments/checkout`, { plan_id: planId, origin_url: originUrl }),
  getStatus: (sessionId) => axios.get(`${API}/payments/status/${sessionId}`),
};

// Admin API
export const adminAPI = {
  getStats: () => axios.get(`${API}/admin/stats`),
  getSystemHealth: () => axios.get(`${API}/admin/system-health`),
  getUsers: () => axios.get(`${API}/admin/users`),
  toggleAdmin: (userId, makeAdmin) => 
    axios.post(`${API}/admin/users/toggle-admin`, { user_id: userId, make_admin: makeAdmin }),
  createProgram: (data) => axios.post(`${API}/admin/programs`, data),
  updateProgram: (id, data) => axios.put(`${API}/admin/programs/${id}`, data),
  deleteProgram: (id) => axios.delete(`${API}/admin/programs/${id}`),
  createModule: (data) => axios.post(`${API}/admin/modules`, data),
  updateModule: (id, data) => axios.put(`${API}/admin/modules/${id}`, data),
  deleteModule: (id) => axios.delete(`${API}/admin/modules/${id}`),
  reorderModules: (itemIds) => axios.post(`${API}/admin/modules/reorder`, { item_ids: itemIds }),
  createLesson: (data) => axios.post(`${API}/admin/lessons`, data),
  updateLesson: (id, data) => axios.put(`${API}/admin/lessons/${id}`, data),
  deleteLesson: (id) => axios.delete(`${API}/admin/lessons/${id}`),
  reorderLessons: (itemIds) => axios.post(`${API}/admin/lessons/reorder`, { item_ids: itemIds }),
  spawnAffiliate: (data) => axios.post(`${API}/admin/affiliates/spawn`, data),
  // Atelier credits (admin quick-actions)
  grantAtelierCredits: (emailOrId, amount) =>
    axios.post(`${API}/atelier/credits/grant-by-email`, {
      email_or_id: emailOrId,
      amount: amount,
    }),
  getAtelierCredits: (emailOrId) =>
    axios.get(`${API}/atelier/credits/by-user/${encodeURIComponent(emailOrId)}`),
  // LLM Universal Key health
  getLlmHealth:   () => axios.get(`${API}/admin/llm-health`),
  probeLlmHealth: () => axios.post(`${API}/admin/llm-health/probe`),
  getLibraryUsage: () => axios.get(`${API}/admin/library/usage`),
};

// Kajabi Integration API
export const kajabiAPI = {
  getStatus: () => axios.get(`${API}/kajabi/status`),
  getCourses: () => axios.get(`${API}/kajabi/courses`),
  syncContent: () => axios.post(`${API}/kajabi/sync`),
  getSyncStatus: () => axios.get(`${API}/kajabi/sync/status`),
  clearCache: () => axios.post(`${API}/kajabi/clear-cache`),
  verifyAccess: () => axios.get(`${API}/kajabi/verify-access`),
  linkAccount: () => axios.post(`${API}/kajabi/link-account`),
};

// Image Library API
export const imageLibraryAPI = {
  getInfo: () => axios.get(`${API}/images`),
  getCategories: () => axios.get(`${API}/images/categories`),
  getCategory: (categoryId) => axios.get(`${API}/images/category/${categoryId}`),
  getRandom: (category = null) => axios.get(`${API}/images/random`, { params: { category } }),
  getByUseCase: (useCase) => axios.get(`${API}/images/use/${useCase}`),
  getById: (imageId) => axios.get(`${API}/images/${imageId}`),
};

// Daily Alignment API
export const alignmentAPI = {
  getToday: () => axios.get(`${API}/alignment/today`),
  complete: (step, reflection = null) => 
    axios.post(`${API}/alignment/complete`, { step, reflection }),
  getHistory: (month) => axios.get(`${API}/alignment/history${month ? `?month=${month}` : ''}`),
  getStats: () => axios.get(`${API}/alignment/stats`),
  // Admin endpoints
  getPrompts: () => axios.get(`${API}/alignment/prompts`),
  createPrompt: (data) => axios.post(`${API}/alignment/prompts`, data),
  updatePrompt: (id, data) => axios.put(`${API}/alignment/prompts/${id}`, data),
  deletePrompt: (id) => axios.delete(`${API}/alignment/prompts/${id}`),
};

// Seed API
export const seedAPI = {
  run: () => axios.post(`${API}/seed`),
};

// Affiliate/Referral API
export const affiliateAPI = {
  getCode: () => axios.get(`${API}/affiliate/code`),
  getStats: () => axios.get(`${API}/affiliate/stats`),
  validateCode: (code) => axios.get(`${API}/affiliate/validate/${code}`),
  trackClick: (code, source) => axios.post(`${API}/affiliate/track-click`, null, { 
    params: { referral_code: code, source } 
  }),
  getLeaderboard: () => axios.get(`${API}/affiliate/leaderboard`),
};

// Challenges API
export const challengesAPI = {
  getAll: (category, search) => axios.get(`${API}/challenges`, { params: { category, search } }),
  getCategories: () => axios.get(`${API}/challenges/categories`),
  getFeatured: () => axios.get(`${API}/challenges/featured`),
  getMyChallenges: () => axios.get(`${API}/challenges/my-challenges`),
  getById: (id) => axios.get(`${API}/challenges/${id}`),
  start: (id) => axios.post(`${API}/challenges/${id}/start`),
  completeDay: (id, day, reflection) => axios.post(`${API}/challenges/${id}/complete-day`, { day, reflection }),
  abandon: (id) => axios.post(`${API}/challenges/${id}/abandon`),
  // Admin
  adminGetAll: (category, search, activeOnly) => 
    axios.get(`${API}/challenges/admin/all`, { params: { category, search, active_only: activeOnly } }),
  adminCreate: (data) => axios.post(`${API}/challenges/admin/create`, data),
  adminUpdate: (id, data) => axios.put(`${API}/challenges/admin/${id}`, data),
  adminDelete: (id) => axios.delete(`${API}/challenges/admin/${id}`),
  adminReorder: (ids) => axios.post(`${API}/challenges/admin/reorder`, ids),
  adminToggleActive: (id) => axios.post(`${API}/challenges/admin/${id}/toggle-active`),
};

export default axios;
